import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Popover,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useExternalRegisterImport } from "./externalRegisterImportProvider";
import {
  formatBytes,
  LARGE_UPLOADED_CSV_TEXT_LIMIT,
  StageUnavailable,
  WorkflowCard,
} from "../shared/ExternalRegisterSettingsUi";

export const SourceCsvStage = () => {
  const { t } = useTranslation();
  const [helpAnchor, setHelpAnchor] = useState<HTMLButtonElement | null>(null);
  const {
    csvText,
    sourceFileName,
    sourceFileSize,
    setCsvText,
    loadCsvFile,
    isReadingCsvFile,
    parseCsv,
    isParsing,
    cancelImport,
    reviewReadonly,
  } = useExternalRegisterImport();
  const isLargeUploadedCsv = Boolean(sourceFileName) && csvText.length > LARGE_UPLOADED_CSV_TEXT_LIMIT;
  const sourceDescription = sourceFileName
    ? t("externalRegister.import.source.loaded.description", {
      fileName: sourceFileName,
      fileSize: formatBytes(sourceFileSize ?? 0),
      characters: csvText.length.toLocaleString(),
    })
    : null;
  const helpOpen = Boolean(helpAnchor);

  return (
    <WorkflowCard title={t("externalRegister.import.source.title")}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Typography variant="body2" color="text.secondary">
            {t("externalRegister.import.source.intro")}
          </Typography>
          <Tooltip title={t("externalRegister.import.source.requirements.buttonLabel")}>
            <IconButton
              size="small"
              aria-label={t("externalRegister.import.source.requirements.ariaLabel")}
              onClick={(event) => setHelpAnchor(event.currentTarget)}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Popover
            open={helpOpen}
            anchorEl={helpAnchor}
            onClose={() => setHelpAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  width: 520,
                  maxWidth: "calc(100vw - 32px)",
                  p: 1.5,
                  borderRadius: 1,
                },
              },
            }}
          >
            <Stack spacing={1.25}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {t("externalRegister.import.source.requirements.headings.required")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("externalRegister.import.source.requirements.requiredDescription")}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {t("externalRegister.import.source.requirements.headings.constrained")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("externalRegister.import.source.requirements.constrainedDescription")}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {t("externalRegister.import.source.requirements.headings.optional")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("externalRegister.import.source.requirements.optionalDescription")}
                </Typography>
              </Box>
            </Stack>
          </Popover>
        </Stack>
        {reviewReadonly && (
          <Alert severity="info" variant="outlined">
            {t("externalRegister.import.source.readOnlyAlert")}
          </Alert>
        )}
        {!reviewReadonly && !csvText.trim() && (
          <StageUnavailable message={t("externalRegister.import.source.unavailable")} />
        )}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={reviewReadonly || isReadingCsvFile}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {isReadingCsvFile ? t("externalRegister.import.source.buttons.reading") : t("externalRegister.import.source.buttons.upload")}
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void loadCsvFile(file);
                event.currentTarget.value = "";
              }}
            />
          </Button>
          {sourceDescription && (
            <Chip
              variant="outlined"
              label={t("externalRegister.import.source.loaded.prefix", { source: sourceDescription })}
              sx={{ justifyContent: "flex-start", maxWidth: "100%", "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }}
            />
          )}
        </Stack>
        {reviewReadonly ? null : isLargeUploadedCsv ? (
          <Stack spacing={1}>
            <Alert severity="info" variant="outlined">
              {t("externalRegister.import.source.largeCsvAlert")}
            </Alert>
            <Paper
              variant="outlined"
              sx={{
                maxHeight: 260,
                overflow: "auto",
                p: 1.25,
                backgroundColor: "grey.50",
                borderRadius: 1,
              }}
            >
              <Typography
                component="pre"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  m: 0,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {csvText.slice(0, LARGE_UPLOADED_CSV_TEXT_LIMIT)}
              </Typography>
            </Paper>
          </Stack>
        ) : (
          <TextField
            label={sourceFileName ? t("externalRegister.import.source.textField.uploadedLabel") : t("externalRegister.import.source.textField.pasteLabel")}
            value={csvText}
            onChange={(event) => setCsvText(event.target.value)}
            disabled={reviewReadonly}
            multiline
            minRows={8}
            fullWidth
            helperText={sourceFileName ? t("externalRegister.import.source.textField.helperUpload") : t("externalRegister.import.source.textField.helperPaste")}
          />
        )}
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            onClick={parseCsv}
            disabled={reviewReadonly || !csvText.trim() || isParsing || isReadingCsvFile}
          >
            {isParsing ? t("externalRegister.import.source.buttons.parsing") : t("externalRegister.import.source.buttons.parse")}
          </Button>
          <Button variant="outlined" color="warning" onClick={cancelImport} disabled={reviewReadonly}>
            {t("externalRegister.import.source.buttons.cancel")}
          </Button>
        </Stack>
      </Stack>
    </WorkflowCard>
  );
};
